import React, { useEffect, useRef } from 'react';

const CustomCursor = () => {
  const cursorRef = useRef(null);
  const outlineRef = useRef(null);
  const requestRef = useRef(null);
  const previousTimeRef = useRef(null);
  const cursorPos = useRef({ x: 0, y: 0 });
  const actualPos = useRef({ x: 0, y: 0 });
  const outlinePos = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const cursor = cursorRef.current;
    const outline = outlineRef.current;

    const handleMouseMove = (e) => {
      cursorPos.current = { x: e.clientX, y: e.clientY };
    };

    const handleMouseEnter = () => {
      cursor.style.opacity = '1';
      outline.style.opacity = '1';
    };

    const handleMouseLeave = () => {
      cursor.style.opacity = '0';
      outline.style.opacity = '0';
    };

    const handleMouseDown = () => {
      cursor.style.transform = 'scale(0.8)';
      outline.style.transform = 'scale(0.8)';
    };

    const handleMouseUp = () => {
      cursor.style.transform = 'scale(1)';
      outline.style.transform = 'scale(1)';
    };

    // Smooth animation loop
    const animate = (time) => {
      if (previousTimeRef.current !== undefined) {
        // Smooth cursor movement
        actualPos.current.x += (cursorPos.current.x - actualPos.current.x) * 0.15;
        actualPos.current.y += (cursorPos.current.y - actualPos.current.y) * 0.15;
        
        // Even smoother outline movement
        outlinePos.current.x += (cursorPos.current.x - outlinePos.current.x) * 0.08;
        outlinePos.current.y += (cursorPos.current.y - outlinePos.current.y) * 0.08;

        cursor.style.left = actualPos.current.x - 4 + 'px';
        cursor.style.top = actualPos.current.y - 4 + 'px';
        
        outline.style.left = outlinePos.current.x - 24 + 'px';
        outline.style.top = outlinePos.current.y - 24 + 'px';
      }
      
      previousTimeRef.current = time;
      requestRef.current = requestAnimationFrame(animate);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseenter', handleMouseEnter);
    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseup', handleMouseUp);

    // Handle hover states
    const handleElementHover = (e) => {
      const target = e.target;
      const isInteractive = target.matches('a, button, input, textarea, select, [role="button"], .interactive');
      
      if (isInteractive) {
        outline.classList.add('hover');
        cursor.classList.add('hover');
      }
    };

    const handleElementLeave = () => {
      outline.classList.remove('hover');
      cursor.classList.remove('hover');
    };

    document.addEventListener('mouseover', handleElementHover);
    document.addEventListener('mouseout', handleElementLeave);

    // Start animation
    requestRef.current = requestAnimationFrame(animate);

    // Hide cursor on touch devices
    const handleTouch = () => {
      cursor.style.display = 'none';
      outline.style.display = 'none';
    };

    window.addEventListener('touchstart', handleTouch);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseenter', handleMouseEnter);
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mouseover', handleElementHover);
      document.removeEventListener('mouseout', handleElementLeave);
      window.removeEventListener('touchstart', handleTouch);
      
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
    };
  }, []);

  return (
    <>
      <style>{`
        @media (pointer: coarse) {
          .cursor,
          .cursor-outline {
            display: none !important;
          }
        }

        .cursor {
          position: fixed;
          width: 8px;
          height: 8px;
          background: var(--blue-600);
          border-radius: 50%;
          pointer-events: none;
          z-index: 10000;
          opacity: 0;
          transition: transform 0.2s ease, opacity 0.3s ease;
          mix-blend-mode: difference;
        }

        .cursor.hover {
          background: var(--blue-500);
          transform: scale(1.5);
        }

        .cursor-outline {
          position: fixed;
          width: 48px;
          height: 48px;
          border: 2px solid var(--blue-600);
          border-radius: 50%;
          pointer-events: none;
          z-index: 9999;
          opacity: 0;
          transition: transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94), 
                      opacity 0.3s ease,
                      border-color 0.3s ease,
                      width 0.3s ease,
                      height 0.3s ease;
        }

        .cursor-outline.hover {
          width: 64px;
          height: 64px;
          border-color: var(--blue-500);
          border-width: 1px;
          background: rgba(59, 130, 246, 0.1);
        }

        [data-theme="dark"] .cursor {
          background: var(--blue-400);
          mix-blend-mode: screen;
        }

        [data-theme="dark"] .cursor-outline {
          border-color: var(--blue-400);
        }

        [data-theme="dark"] .cursor-outline.hover {
          border-color: var(--blue-300);
          background: rgba(96, 165, 250, 0.1);
        }

        /* Cursor effects for specific elements */
        .cursor-grow {
          cursor: none;
        }

        .cursor-grow:hover ~ .cursor {
          transform: scale(2);
        }

        .cursor-grow:hover ~ .cursor-outline {
          transform: scale(1.2);
        }

        /* Hide default cursor globally when custom cursor is active */
        @media (pointer: fine) {
          * {
            cursor: none !important;
          }
        }
      `}</style>
      <div ref={cursorRef} className="cursor"></div>
      <div ref={outlineRef} className="cursor-outline"></div>
    </>
  );
};

export default CustomCursor;
